import { useEffect, useState, useRef } from 'react';

interface UseAnimatedCounterOptions {
    duration?: number; // Animation duration in milliseconds
    decimals?: number; // Number of decimal places
    animateOnMount?: boolean; // Animate from 0 on first render instead of snapping to target
}

/**
 * Custom hook that animates a number from its current value to a target value
 * Similar to poker counter animations - smooth counting up/down
 */
export const useAnimatedCounter = (targetValue: number, options: UseAnimatedCounterOptions = {}): number => {
    const { duration = 800, decimals = 1, animateOnMount = false } = options;
    const [displayValue, setDisplayValue] = useState(() => {
        if (animateOnMount) return 0;
        // Handle NaN, undefined, or null
        return isNaN(targetValue) || targetValue === null || targetValue === undefined ? 0 : targetValue;
    });
    const animationFrameRef = useRef<number | null>(null);
    const startValueRef = useRef(displayValue);
    const startTimeRef = useRef<number | null>(null);
    const isFirstRender = useRef(true);

    useEffect(() => {
        // Handle NaN, undefined, or null
        const safeTargetValue = isNaN(targetValue) || targetValue === null || targetValue === undefined ? 0 : targetValue;

        // On first render, set immediately without animation — unless the caller opted into
        // animating from 0 on mount (e.g. a component that mounts already holding its final data)
        if (isFirstRender.current) {
            isFirstRender.current = false;
            if (!animateOnMount) {
                setDisplayValue(safeTargetValue);
                startValueRef.current = safeTargetValue;
                return;
            }
        }

        // If value hasn't changed, don't animate
        if (safeTargetValue === displayValue) {
            return;
        }

        // Cancel any ongoing animation
        if (animationFrameRef.current !== null) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        // Reset animation state
        startValueRef.current = displayValue;
        startTimeRef.current = null;

        const animate = (currentTime: number) => {
            if (startTimeRef.current === null) {
                startTimeRef.current = currentTime;
            }

            const elapsed = currentTime - startTimeRef.current;
            const progress = Math.min(elapsed / duration, 1);

            // Easing function for smooth animation (ease-out cubic)
            const easeOut = 1 - Math.pow(1 - progress, 3);

            const currentValue = startValueRef.current + (safeTargetValue - startValueRef.current) * easeOut;

            // Round to specified decimals
            const roundedValue = parseFloat(currentValue.toFixed(decimals));

            setDisplayValue(roundedValue);

            if (progress < 1) {
                animationFrameRef.current = requestAnimationFrame(animate);
            } else {
                // Ensure final value is exact
                setDisplayValue(safeTargetValue);
                animationFrameRef.current = null;
            }
        };

        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrameRef.current !== null) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, [targetValue, duration, decimals, animateOnMount]);

    return displayValue;
};
